import {
    ALGOLIA_INDEX_NAME,
    useCancelSubscription,
    useItemsInRow,
    useOrganizationSubscriptions,
    usePurchaseSubscription,
    useSearch,
    useTaggedVideos,
    useTivioData,
    useTransactionPayment,
    useUser,
    useVideo,
} from '@tivio/sdk-react'
import React, { FC, PropsWithChildren, useEffect } from 'react'

import './App.css'

import type { Purchase } from '@tivio/sdk-react'

const PlayerExample = () => {
    const bundle = useTivioData()

    if (!bundle?.components?.WebPlayer) {
        return <p>Loading...</p>
    }

    return (
        <>
            <h1>Player example</h1>
            <div
                style={{
                    height: 500,
                    width: 500,
                }}
            >
                <bundle.components.WebPlayer
                    id="player1"
                    className="web-player"
                    isSameSizeAsParent
                    source="/videos/nsBSyif79l6B8aC0CgY3" // Oktagon prod "ND7: Ukončím Viktora"
                    // source="/videos/QmU4acbup7Tu0Be0oiZ2" // DRM oktagon prod
                    // source="/videos/Dha6ajYgU8dTPQoPETnL" // live joj
                />
            </div>
        </>
    )
}

const QerkoSubscriptionExample = () => {
    const { paymentError, paymentInfo, paymentStatus } = usePurchaseSubscription('IepP8r3FwF9uTWgIHk7Y') // Oktagon prod "Bez reklam" subscription

    return (
        <>
            <h1>Qerko subscription example</h1>
            <p>paymentError: {paymentError}</p>
            <p>paymentStatus: {paymentStatus}</p>
            <p>webPaymentGatewayLink: <a href={paymentInfo?.webPaymentGatewayLink}>{paymentInfo?.webPaymentGatewayLink}</a></p>
            <img
                height={150}
                width={150}
                src={paymentInfo?.paymentQRCodeUri}
                alt="qerko"
            />
        </>
    )
}

const QerkoTransactionExample = () => {
    const { paymentError, paymentInfo, paymentStatus } = useTransactionPayment('QmU4acbup7Tu0Be0oiZ2', 'tDyDl16LB04MRx0oVlXQ') // Oktagon prod "Oktagon 29" video

    return (
        <>
            <h1>Qerko transaction example</h1>
            <p>paymentError: {paymentError}</p>
            <p>paymentStatus: {paymentStatus}</p>
            <p>webPaymentGatewayLink: <a href={paymentInfo?.webPaymentGatewayLink}>{paymentInfo?.webPaymentGatewayLink}</a></p>
            <img
                height={150}
                width={150}
                src={paymentInfo?.paymentQRCodeUri}
                alt="qerko"
            />
        </>
    )
}

const CancelSubscriptionExample = () => {
    const bundle = useTivioData()
    const { cancelSubscription, cancellationInfo, error } = useCancelSubscription('IepP8r3FwF9uTWgIHk7Y') // Oktagon prod "Bez reklam" subscription

    if (!bundle || !bundle.components || bundle.state !== "ready") {
        return null
    }

    return (
        <>
            <h1>Qerko cancel subscription example</h1>
            <p>error: {error}</p>
            <p>cancellationInfo: { JSON.stringify(cancellationInfo) }</p>
            <button onClick={() => { cancelSubscription() }}>Cancel subscription</button>
        </>
    )
}

const Search = () => {
    const bundle = useTivioData()
    const { search, pagination, error, isLoading } = useSearch(ALGOLIA_INDEX_NAME.VIDEOS)

    if (!bundle || !bundle.components || bundle.state !== "ready") {
        return null
    }

    const itemMapper = (item: any) => ({
        id: item?.id,
        ref: item?.ref?.path,
        name: item?.name,
        description: item?.description,
    })

    return (
        <>
            <h1>Search example</h1>
            <p>isLoading: {isLoading ? 'true' : 'false'}</p>
            <p>error: {JSON.stringify(error)}</p>
            <input type={'search'} onChange={(e) => search(e.target.value)}/>
            <pre>items: { JSON.stringify(pagination?.items.map(itemMapper), null, 2) }</pre>
        </>
    )
}


const UseItemsInRowExample = () => {
    const { pagination: rowData } = useItemsInRow({rowId: 'row-4', options: { fetchTags: true, noLimit: true }})

    console.log('rowData: ', rowData);
    console.log('items: ', rowData?.items)

    rowData?.items.forEach(item => {
        console.log('name: ', item.name)
    })

    return <h1>UseItemsInRowExample (see console)</h1>
}

const UseOrganizationSubscriptionsExample = () => {
    const { subscriptions } = useOrganizationSubscriptions();

    return (
        <>
            <h1>UseOrganizationSubscriptionsExample</h1>
            <p>subscriptions: { subscriptions.map( subscription => subscription.name + ', ') }</p>
        </>
    )

}

const UseTaggedVideosExample = () => {
    const { pagination: taggedVideos } = useTaggedVideos(["oktagon-laky-royal"], { noLimit: true, fetchTags: true })

    console.log('taggedVideos: ', taggedVideos);

    return <h1>UseTaggedVideosExample (see console)</h1>

}

const UseLoginExample=()=>{
    const bundle = useTivioData()
    const { user } = useUser()

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const target = e.target as typeof e.target & {
            email: { value: string };
            password: { value: string };
        };
        const email = target.email.value;
        const password = target.password.value;
        await bundle.auth?.signInWithEmailAndPassword(email, password)
    }

    const handleOtherDomain = async () => {
        // window.open('https://sub.sdk-sso-test.tivio.studio/', "_blank", "noreferrer");
        window.open('https://sdk-sso-test.tivio.studio/', "_blank", "noreferrer");
    }

    return user ?
        <>
            {user.email}<br/>
            <button onClick={() => bundle.auth?.signOut()}>Logout</button> <br/>
            <button type="button" value="Open other domain" onClick={handleOtherDomain}>Open other domain</button>
        </>
        : <form onSubmit={handleSubmit} style={{display: 'flex', flexDirection: 'column', alignItems: 'start'}}>
            <label>Username:
                <input
                    type="email"
                    name="email"
                />
            </label>

            <label>Password:
                <input
                    type="password"
                    name="password"
                />
            </label>
            <input type="submit" value="Login"/>
            <input type="button" value="Open other domain" onClick={handleOtherDomain}/>
        </form>
}

const UseUserExample = () => {
    const bundle = useTivioData()
    const { user } = useUser()

    // !called && bundle.settings.setUser("51458", "eyJhbGciOiJSUzI1NiIsImtpZCI6ImY1NWUyOTRlZWRjMTY3Y2Q5N2JiNWE4MTliYmY3OTA2MzZmMTIzN2UiLCJ0eXAiOiJKV1QifQ.eyJjb21wYW55SWQiOjEsImlkIjo1MTQ1OCwic3lzQWNjZXNzR3JvdXBJZCI6NSwiaXNzIjoiaHR0cHM6Ly9zZWN1cmV0b2tlbi5nb29nbGUuY29tL29rdGFnb25wcm9kIiwiYXVkIjoib2t0YWdvbnByb2QiLCJhdXRoX3RpbWUiOjE2MzY2NDU5NDMsInVzZXJfaWQiOiJHbUx5Z0w2ajFTYVN5M01iRHU5a3ZoV3NsVjYyIiwic3ViIjoiR21MeWdMNmoxU2FTeTNNYkR1OWt2aFdzbFY2MiIsImlhdCI6MTYzNjY0OTgyMSwiZXhwIjoxNjM2NjUzNDIxLCJlbWFpbCI6InRlc3QudGl2aW8uMUB6d2l6dS5jb20iLCJlbWFpbF92ZXJpZmllZCI6ZmFsc2UsImZpcmViYXNlIjp7ImlkZW50aXRpZXMiOnsiZW1haWwiOlsidGVzdC50aXZpby4xQHp3aXp1LmNvbSJdfSwic2lnbl9pbl9wcm92aWRlciI6InBhc3N3b3JkIn19.K-sTzFrFKA_zyAGNiH9hvD-P6s7LV-c1XVhgPE6F8mRQVn9g8upupSOVNmzViRcxRe0_ns-VmjWqtOGS5LB456WNWhkI6qhlsRi7h0RrRLIorIxJuOG_c3fBJlbY3F1KckMaJyOcpihem_UtsrjUlFkdD1JF7Ru7GvjuZ5hPqaxxBCux7CP7HeT2OQRitVtw9UyvlUzMoQuWOmtE7yhl97TdkWeJDQ0_8iKhCf0LWLncdL_YUVTexWA00DtWF08O44HAElNTZQor7RYXBuI69HbxlmJW2RmQWgTMb706eQlIkWdFFMsmYxsoAl13hcQe1i1D58EuDzy77-AqhnICzg")
    // bundle.auth?.initializeUser()

    useEffect(() => {
        (async () => {
            if (!bundle.auth || user?.isSignedIn) {
                return
            }
            // await bundle.auth?.createUserWithEmailAndPassword('sdk-react-test-user@tiv.io', 'ativio', 'sdk-react-test-user')
            // await bundle.auth?.signInWithEmailAndPassword('tivio.internal@tiv.io', 'Heslo1234') // prod oktagon
            // await bundle.auth?.signInWithEmailAndPassword('patrik.helta@nangu.tv', '123123') // prod oktagon
            console.log('logged in 1111')

            await bundle.auth?.signInWithEmailAndPassword('sdk-react-test-user@tiv.io', 'ativio')
            // await bundle.auth?.initializeUser()
            console.log('logged in')
            // setTimeout(async () => {
            //     await bundle.auth?.signOut()
            //     console.log('logged out')
            // }, 5000);
        })()
    }, [bundle.auth, user?.isSignedIn])

    const formatPurchase = (purchases?: Purchase[]): string => {
        return JSON.stringify(purchases?.map(p => ({
            monetizationId: p.monetizationId,
            type: p.type,
            status: p.status,
            isPurchased: p.isPurchased,
            videoId: p.videoId,
            expirationDate: p.expirationDate,
            created: p.created ? new Date(p.created) : '',
            updated: p.updated ? new Date(p.updated) : '',
        })), null, 2)
    }

    return (
        <>
            <h1>UseUserExample</h1>
            <p>user: { user?.email }</p>
            <p>isSignedIn: { user?.isSignedIn ? 'true' : 'false' }</p>
            <pre>purchasedSubscriptions: { formatPurchase(user?.purchasedSubscriptions) }</pre>
            <pre>purchasedVods: { formatPurchase(user?.purchasedVods) }</pre>
            <pre>purchases: { formatPurchase(user?.purchases) }</pre>
        </>
    )

}

const UseVideoExample = () => {
    const { data } = useVideo('vt4MSXgFOQFaH2EgbtqQ') // Oktagon prod "Xavier vs. Toledo"

    console.log('videoData: ', data);

    return <h1>UseVideoExample (see console)</h1>
}

const App = (): React.ReactNode => {
    console.log('App')
    return (
        <React.Fragment>
            {/*<PlayerExample/>*/}
            {/*<QerkoSubscriptionExample/>*/}
            {/*<QerkoTransactionExample/>*/}
            {/*<CancelSubscriptionExample/>*/}
            {/*<Search/>*/}
            {/*<UseItemsInRowExample/>*/}
            {/*<UseOrganizationSubscriptionsExample/>*/}
            {/*<UseTaggedVideosExample/>*/}
            {/*<UseUserExample/>*/}
            {/*<UseVideoExample/>*/}
            <UseLoginExample/>
        </React.Fragment>
    )
}

export default App
